import { DeleteOutlined, InfoCircleOutlined, UserOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Form, Input, InputNumber, Modal, Radio, Rate, Select, Spin, Typography } from 'antd';
import { isNil, omitBy } from 'lodash-es';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useFetcher, useLoaderData } from 'react-router-dom';
import { defaultProductMargin, generateRouteUrl } from '../../../library/constants';
// import { Server } from '../../../library/constants/server';
import { getVendorDetails } from '../../../api/vendor/vendor';
import { ItemPriceVendorQuotationStatus, getVendorQuotationStatus } from '../../../library/constants/VendorItemPriceStatuses.js';
import { ERP_CURRENCY } from '../../../library/constants/dynamic.js';
import { convertDataTypes } from '../../../library/helpers/index.js';
import { roundOfNumber } from '../../../library/utilities/intl.js';
import { calculateSelllingMarginPercent } from '../../../library/utilities/math.js';
import { SmallSection } from '../../design';
import { Currency, CurrencyConversion, Percent } from '../../inputs';
import styles from './vendor-item-prices.module.scss';
import './vendor-item-prices.scss';

const calculateSellingPrice = (quantity, values) => {
    const { purchase_price = 0.00, finisher_price = 0.00, handling_charge = 0.00, shipping_charge = 0.00, margin = 0 } = values
    const totalCost = (purchase_price + finisher_price + (handling_charge + shipping_charge) / quantity)
    return roundOfNumber((totalCost / (1 - margin / 100)), 2)
}
const calculateMargin = (values, customPrice, quantity) => {
    const { purchase_price = 0.00, finisher_price = 0.00, handling_charge = 0.00, shipping_charge = 0.00 } = values
    // const totalPrice = ((purchase_price * quantity) + finisher_price + handling_charge + shipping_charge)
    const totalCost = (purchase_price + finisher_price + (handling_charge + shipping_charge) / quantity)
    return calculateSelllingMarginPercent(customPrice, totalCost)
}

const VendorQuotationForm = ({ vendor, disabled, ...otherProps }) => {
    const fetcher = useFetcher()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [loadingContacts, setLoadingContacts] = useState(false)
    const [vendorData, setVendorData] = useState(null)
    const [selectedContactIds, setSelectedContactIds] = useState([])

    const isResend = vendor?.quotationAssignment?.vendor_quotation_status >= ItemPriceVendorQuotationStatus.VENDOR_QUOTATION_SENT
    const buttonText = isResend ? 'Resend Request' : 'Send Request'

    const handleOpenModal = async () => {
        setIsModalOpen(true)
        setLoadingContacts(true)
        try {
            const data = await getVendorDetails(vendor.id)
            setVendorData(data)
            const contacts = data?.contacts || []
            let defaultId = null
            if (data?.default_enquiry_contact_id && contacts.some(c => c.id === data.default_enquiry_contact_id)) {
                defaultId = data.default_enquiry_contact_id
            } else {
                const firstEnquiry = contacts.find(c => (c.role_id || c.role?.id || c.role) === 'enquiry')
                if (firstEnquiry) {
                    defaultId = firstEnquiry.id
                } else if (data?.primary_contact_id && contacts.some(c => c.id === data.primary_contact_id)) {
                    defaultId = data.primary_contact_id
                } else {
                    const firstPrimary = contacts.find(c => (c.role_id || c.role?.id || c.role) === 'primary')
                    defaultId = firstPrimary ? firstPrimary.id : (contacts[0]?.id || null)
                }
            }
            setSelectedContactIds(defaultId ? [defaultId] : [])
        } catch (error) {
            console.error('Failed to load vendor details for enquiry contact', error)
        } finally {
            setLoadingContacts(false)
        }
    }

    const handleClose = () => {
        setIsModalOpen(false)
    }

    const handleSubmit = () => {
        if (!selectedContactIds.length) return
        fetcher.submit({
            action: 'sendQuotationRequest',
            vendorId: vendor.id,
            vendorContactIds: JSON.stringify(selectedContactIds),
            vendorContactId: selectedContactIds[0] || undefined,
        }, {
            method: 'post',
            encType: 'application/json',
        })
        setIsModalOpen(false)
    }

    const contacts = vendorData?.contacts || []
    const selectedContacts = contacts.filter(c => selectedContactIds.includes(c.id))

    const renderRoleBadge = (isDefaultEnquiry, role, isPrimaryFallback = false) => {
        if (isDefaultEnquiry) {
            return (
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 500,
                        lineHeight: '16px',
                        background: '#eff6ff',
                        color: '#2563eb',
                        border: '1px solid #dbeafe',
                        whiteSpace: 'nowrap',
                    }}
                >
                    Default Enquiry
                </span>
            )
        }
        if (role === 'order') {
            return (
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 500,
                        lineHeight: '16px',
                        background: '#faf5ff',
                        color: '#7e22ce',
                        border: '1px solid #f3e8ff',
                        whiteSpace: 'nowrap',
                    }}
                >
                    Order
                </span>
            )
        }
        if (role === 'enquiry') {
            return (
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 500,
                        lineHeight: '16px',
                        background: '#f0fdfa',
                        color: '#0f766e',
                        border: '1px solid #ccfbf1',
                        whiteSpace: 'nowrap',
                    }}
                >
                    Enquiry
                </span>
            )
        }
        if (role === 'primary' || isPrimaryFallback) {
            return (
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 500,
                        lineHeight: '16px',
                        background: '#f8fafc',
                        color: '#475569',
                        border: '1px solid #e2e8f0',
                        whiteSpace: 'nowrap',
                    }}
                >
                    Primary
                </span>
            )
        }
        return null
    }

    const renderSelectTag = ({ value, closable, onClose }) => {
        const contact = contacts.find(c => c.id === value)
        const name = contact ? [contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.email : ''
        const isDef = vendorData?.default_enquiry_contact_id === value
        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 8px',
                    margin: '2px 4px 2px 0',
                    background: isDef ? '#eff6ff' : '#f8fafc',
                    border: isDef ? '1px solid #bfdbfe' : '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#1e293b',
                    fontWeight: 500,
                }}
            >
                <span>{name}</span>
                {isDef && (
                    <span style={{ fontSize: '10px', color: '#2563eb', fontWeight: 600 }}>Default</span>
                )}
                {closable && (
                    <span
                        onClick={onClose}
                        style={{
                            cursor: 'pointer',
                            color: '#94a3b8',
                            marginLeft: '2px',
                            fontSize: '12px',
                            lineHeight: 1,
                        }}
                    >
                        ×
                    </span>
                )}
            </span>
        )
    }

    const contactOptions = contacts.map(c => {
        const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Unnamed Contact'
        const isDefaultEnquiry = vendorData?.default_enquiry_contact_id === c.id
        const contactRole = c.role_id || c.role?.id || c.role
        const isEnquiry = contactRole === 'enquiry'
        const isOrder = contactRole === 'order'
        const isPrimary = contactRole === 'primary' || (!isEnquiry && !isOrder && vendorData?.primary_contact_id === c.id)

        const roleText = isDefaultEnquiry
            ? 'Default Enquiry'
            : isOrder
                ? 'Order'
                : isEnquiry
                    ? 'Enquiry'
                    : isPrimary
                        ? 'Primary'
                        : ''

        return {
            value: c.id,
            label: (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', width: '100%' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span style={{ fontWeight: 500 }}>{name}</span>
                        <span style={{ color: '#94a3b8', marginLeft: '6px', fontSize: '12px' }}>&lt;{c.email || 'no email'}&gt;</span>
                    </span>
                    {renderRoleBadge(isDefaultEnquiry, contactRole, isPrimary)}
                </div>
            ),
            searchKey: `${name} ${c.email || ''} ${roleText}`.toLowerCase(),
        }
    })

    return (
        <>
            <Button
                type="primary"
                onClick={handleOpenModal}
                disabled={disabled}
                loading={fetcher.state !== 'idle'}
                {...otherProps}
            >
                {buttonText}
            </Button>

            <Modal
                title={isResend ? 'Resend Quotation Request' : 'Send Quotation Request'}
                open={isModalOpen}
                onCancel={handleClose}
                onOk={handleSubmit}
                okText={buttonText}
                okButtonProps={{ disabled: selectedContactIds.length === 0 }}
                confirmLoading={fetcher.state !== 'idle'}
                destroyOnClose
                width={560}
            >
                {loadingContacts ? (
                    <div style={{ textAlign: 'center', padding: '32px 0' }}>
                        <Spin tip="Loading vendor contacts..." />
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
                        <div>
                            <Typography.Text type="secondary">Vendor</Typography.Text>
                            <div style={{ fontWeight: 600, fontSize: '15px' }}>
                                {vendorData?.company || vendor?.company}
                            </div>
                        </div>

                        <div>
                            <Typography.Text strong style={{ display: 'block', marginBottom: '6px' }}>
                                Select Recipient Contact(s):
                            </Typography.Text>
                            {contactOptions.length > 0 ? (
                                <Select
                                    mode="multiple"
                                    showSearch
                                    allowClear
                                    style={{ width: '100%' }}
                                    value={selectedContactIds}
                                    onChange={setSelectedContactIds}
                                    options={contactOptions}
                                    placeholder="Search and select contact(s)"
                                    tagRender={renderSelectTag}
                                    filterOption={(input, option) => {
                                        const query = (input || '').toLowerCase().trim()
                                        if (!query) return true
                                        return (option?.searchKey || '').includes(query)
                                    }}
                                    maxTagCount="responsive"
                                />
                            ) : (
                                <Typography.Text type="secondary">
                                    No individual contacts found. Email will be sent to the vendor company address.
                                </Typography.Text>
                            )}
                        </div>

                        {selectedContacts.length === 0 ? (
                            <div style={{
                                background: '#fffbe6',
                                border: '1px solid #ffe58f',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: '#d46b08',
                                fontSize: '13px',
                            }}>
                                <InfoCircleOutlined style={{ fontSize: '15px' }} />
                                <span>Please select at least one recipient contact to send the quotation request.</span>
                            </div>
                        ) : (
                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                maxHeight: '200px',
                                overflowY: 'auto'
                            }}>
                                <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 600 }}>
                                    {selectedContacts.length === 1 ? 'Recipient (1 contact):' : `Recipients (${selectedContacts.length} contacts):`}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {selectedContacts.map(c => {
                                        const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email
                                        const isDef = vendorData?.default_enquiry_contact_id === c.id
                                        const cRole = c.role_id || c.role?.id || c.role
                                        const isOrd = cRole === 'order'
                                        const isEnq = cRole === 'enquiry'
                                        const isPrim = cRole === 'primary' || (!isOrd && !isEnq && vendorData?.primary_contact_id === c.id)

                                        return (
                                            <div
                                                key={c.id}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    padding: '6px 10px',
                                                    background: '#ffffff',
                                                    borderRadius: '6px',
                                                    border: '1px solid #f1f5f9',
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                                                    <UserOutlined style={{ color: '#6366f1', fontSize: '13px' }} />
                                                    <span style={{ fontWeight: 600, fontSize: '13px', color: '#1e293b' }}>{name}</span>
                                                    {renderRoleBadge(isDef, cRole, isPrim)}
                                                </div>
                                                <span style={{ color: '#64748b', fontSize: '12px', marginLeft: '12px', flexShrink: 0 }}>
                                                    {c.email || 'no email'}
                                                </span>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}

                        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                            Note: Selecting contact(s) here applies only to this item&apos;s price enquiry and does not change the vendor&apos;s default enquiry contact.
                        </Typography.Text>
                    </div>
                )}
            </Modal>
        </>
    )
}
VendorQuotationForm.propTypes = {
    vendor: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
}

const VendorItemQuantityPriceRow = ({ rowKey, quantity, initialValues = {}, vendorCurrencyCode, projectCurrencyCode, onUpdate, onEditingChange, requireFinisher, finisherCurrencyCode, canUpdateQuotation, websitePricesAvailable, vendorPricesAvailable, disabled }) => {
    const [form] = Form.useForm()
    const customSellingPriceValue = Form.useWatch('custom_selling_price_unit', form)

    const isFocusedRef = useRef(false)
    const hasUnsavedChangesRef = useRef(false)
    const latestValuesRef = useRef({})
    const lastSavedValuesRef = useRef({})
    const blurTimeoutRef = useRef(null)
    const rowIdentifier = rowKey || `vendor_row_qty_${quantity}`

    const priceColumnsCount = 9 + (requireFinisher * 1)

    const parsedInitialValues = useMemo(() => {
        return convertDataTypes(initialValues, {
            custom_selling_price_unit: parseFloat,
            finisher_price: parseFloat,
            handling_charge: parseFloat,
            margin: parseFloat,
            purchase_price: parseFloat,
            quantity: parseInt,
            quotation_price_reference: parseInt,
            sales_price: parseFloat,
            shipping_charge: parseFloat,
            status: parseInt,
            total_price: parseFloat,
            total_price_incl_vat: parseFloat,
            vat: parseFloat,
            vat_rate: parseFloat,
        })
    }, [initialValues, vendorCurrencyCode, projectCurrencyCode])

    const commitSave = useCallback((valuesToSave) => {
        if (window.__isNavigatingAway) return
        const values = valuesToSave || latestValuesRef.current
        if (!values || !hasUnsavedChangesRef.current) return

        const currentClean = omitBy(values, isNil)
        const lastSavedClean = omitBy(lastSavedValuesRef.current, isNil)

        const keysToCompare = [
            'purchase_price', 'finisher_price', 'handling_charge', 'shipping_charge',
            'margin', 'custom_selling_price_unit', 'quotation_price_reference'
        ]
        const hasDiff = keysToCompare.some(k => currentClean[k] !== lastSavedClean[k])

        if (hasDiff) {
            lastSavedValuesRef.current = { ...currentClean }
            hasUnsavedChangesRef.current = false
            onUpdate(currentClean)
            onEditingChange?.(false, rowIdentifier)
        } else {
            hasUnsavedChangesRef.current = false
            onEditingChange?.(false, rowIdentifier)
        }
    }, [onUpdate, onEditingChange, rowIdentifier])

    useEffect(() => {
        // Only sync from server when user is NOT currently focused and has no unsaved changes
        if (!isFocusedRef.current && !hasUnsavedChangesRef.current) {
            form.setFieldsValue(parsedInitialValues)
            latestValuesRef.current = { ...parsedInitialValues }
            lastSavedValuesRef.current = { ...parsedInitialValues }
        }
    }, [parsedInitialValues])

    useEffect(() => {
        const handleDiscard = () => {
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current)
                blurTimeoutRef.current = null
            }
            hasUnsavedChangesRef.current = false
            isFocusedRef.current = false
            onEditingChange?.(false, rowIdentifier)
            form.setFieldsValue(parsedInitialValues)
            latestValuesRef.current = { ...parsedInitialValues }
        }
        window.addEventListener('discard-project-item-edits', handleDiscard)
        return () => {
            window.removeEventListener('discard-project-item-edits', handleDiscard)
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current)
            }
            onEditingChange?.(false, rowIdentifier)
        }
    }, [parsedInitialValues, onEditingChange, rowIdentifier])

    const vendorCurrencyDiffers = vendorCurrencyCode !== ERP_CURRENCY
    const projectCurrencyDiffers = projectCurrencyCode !== ERP_CURRENCY
    const finisherCurrencyDiffers = finisherCurrencyCode && finisherCurrencyCode !== ERP_CURRENCY
    const vendorCurrencyRate = initialValues?.conversion_rate?.[vendorCurrencyCode]
    const projectCurrencyRate = initialValues?.conversion_rate?.[projectCurrencyCode]
    const finisherCurrencyRate = finisherCurrencyCode && initialValues?.conversion_rate?.[finisherCurrencyCode]
    const canUpdateCosts = true

    const handleValueChange = (changedFields, values) => {
        const fieldName = Object.keys(changedFields)[0];
        const updatedValues = omitBy(values, isNil)
        if (canUpdateQuotation) {
            if (fieldName === 'custom_selling_price_unit') {
                // Update selling price directly
                const _margin = calculateMargin(values, changedFields[fieldName], quantity)
                form.setFieldValue('margin', _margin)
                updatedValues.margin = _margin
            }
            else if (fieldName !== 'quotation_price_reference') {
                const _customSellingPrice = calculateSellingPrice(quantity, values)
                form.setFieldValue('custom_selling_price_unit', _customSellingPrice)
                updatedValues.custom_selling_price_unit = _customSellingPrice
            }
            const total_price = (updatedValues.custom_selling_price_unit ?? 0) * quantity
            const vat_amount = total_price * ((parsedInitialValues.vat_rate || 0) / 100)
            form.setFieldValue('total_price_incl_vat', total_price + vat_amount)
        }
        else {
            // Adjust margin to keep the sales price same
            const _margin = calculateMargin(values, parsedInitialValues.custom_selling_price_unit, quantity)
            form.setFieldValue('margin', _margin)
            updatedValues.margin = _margin
        }

        latestValuesRef.current = { ...updatedValues }
        hasUnsavedChangesRef.current = true
        onEditingChange?.(true, rowIdentifier)

        // Radio button selections (Website / SharePoint / Vendor) are discrete clicks, save immediately
        if (fieldName === 'quotation_price_reference') {
            commitSave(updatedValues)
        }
    }

    const handleFocus = () => {
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current)
            blurTimeoutRef.current = null
        }
        isFocusedRef.current = true
    }

    const handleBlur = () => {
        if (window.__isNavigatingAway) {
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current)
                blurTimeoutRef.current = null
            }
            return
        }

        // Commit save immediately when any individual field loses focus
        if (hasUnsavedChangesRef.current) {
            commitSave()
        }

        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current)
        }
        blurTimeoutRef.current = setTimeout(() => {
            if (window.__isNavigatingAway) return
            const formElement = document.querySelector(`form[name="vendor-price-item-${quantity}"]`)
            const isStillInside = formElement && formElement.contains(document.activeElement)
            if (!isStillInside) {
                isFocusedRef.current = false
                if (hasUnsavedChangesRef.current) {
                    commitSave()
                } else {
                    onEditingChange?.(false, rowIdentifier)
                }
            }
        }, 150)
    }

    return (
        <Form
            form={form}
            initialValues={{ ...parsedInitialValues }}
            name={`vendor-price-item-${quantity}`}
            onValuesChange={handleValueChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
        >
            <div className={`${requireFinisher ? styles['vendorPriceRow--requireFinisher'] : styles.vendorPriceRow} vendor-price-row`} style={{ '--columns': priceColumnsCount }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <Form.Item>
                        <InputNumber value={quantity} disabled controls={false} />
                    </Form.Item>
                    {vendorCurrencyDiffers && (
                        <div>Conversion = x {roundOfNumber(vendorCurrencyRate, 3)}</div>
                    )}
                </div>
                <Form.Item name="purchase_price">
                    {vendorCurrencyDiffers ?
                        <CurrencyConversion currency={ERP_CURRENCY} secondCurrency={vendorCurrencyCode} conversionRate={vendorCurrencyRate} precision={3} controls={false} disabled={disabled || !canUpdateCosts} /> :
                        <Currency currency={ERP_CURRENCY} precision={3} controls={false} disabled={disabled || !canUpdateCosts} />
                    }
                </Form.Item>
                {requireFinisher === true ? (
                    <Form.Item name="finisher_price">
                        {finisherCurrencyDiffers ?
                            <CurrencyConversion currency={ERP_CURRENCY} secondCurrency={finisherCurrencyCode} conversionRate={finisherCurrencyRate} precision={3} controls={false} disabled={disabled || !canUpdateCosts} /> :
                            <Currency currency={ERP_CURRENCY} precision={3} controls={false} disabled={disabled || !canUpdateCosts} />
                        }
                    </Form.Item>
                ) : <div style={{ display: 'none' }}></div>}
                <Form.Item name="handling_charge">
                    <Currency currency={ERP_CURRENCY} precision={3} controls={false} disabled={disabled || !canUpdateCosts} />
                    {/* {vendorCurrencyDiffers ?
                        <CurrencyConversion currency={ERP_CURRENCY} secondCurrency={vendorCurrencyCode} conversionRate={vendorCurrencyRate} precision={3}  controls={false} /> :
                    } */}
                </Form.Item>
                <Form.Item name="shipping_charge">
                    {vendorCurrencyDiffers ?
                        <CurrencyConversion currency={ERP_CURRENCY} secondCurrency={vendorCurrencyCode} conversionRate={vendorCurrencyRate} precision={3} controls={false} disabled={disabled || !canUpdateCosts} /> :
                        <Currency currency={ERP_CURRENCY} precision={3} controls={false} disabled={disabled || !canUpdateCosts} />
                    }
                </Form.Item>
                <Form.Item name="margin">
                    <Percent type='percent' controls={false} disabled={disabled || !canUpdateQuotation} />
                </Form.Item>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <Form.Item name="custom_selling_price_unit">
                        <Currency currency={ERP_CURRENCY} controls={false} disabled={disabled || !canUpdateQuotation} />
                    </Form.Item>
                    {projectCurrencyDiffers && (
                        <div>Conversion = x {roundOfNumber(projectCurrencyRate, 3)}</div>
                    )}
                </div>
                <Form.Item >
                    {projectCurrencyDiffers ?
                        <CurrencyConversion currency={ERP_CURRENCY} value={customSellingPriceValue} secondCurrency={projectCurrencyCode} conversionRate={projectCurrencyRate} disabled controls={false} /> :
                        <Currency currency={ERP_CURRENCY} value={customSellingPriceValue} disabled controls={false} />
                    }
                </Form.Item>
                <Form.Item >
                    {projectCurrencyDiffers ?
                        <CurrencyConversion currency={ERP_CURRENCY} value={customSellingPriceValue * quantity} secondCurrency={projectCurrencyCode} conversionRate={projectCurrencyRate} onChange={val => {
                            form.setFieldValue('custom_selling_price_unit', val / quantity)
                            handleValueChange({ custom_selling_price_unit: val / quantity }, form.getFieldsValue())
                        }} disabled={disabled || !canUpdateQuotation} /> :
                        <Currency currency={ERP_CURRENCY} value={customSellingPriceValue * quantity} onChange={val => {
                            form.setFieldValue('custom_selling_price_unit', val / quantity)
                            handleValueChange({ custom_selling_price_unit: val / quantity }, form.getFieldsValue())
                        }} disabled={disabled || !canUpdateQuotation} />
                    }
                </Form.Item>
                <Form.Item name="total_price_incl_vat">
                    {projectCurrencyDiffers ?
                        <CurrencyConversion currency={ERP_CURRENCY} secondCurrency={projectCurrencyCode} conversionRate={projectCurrencyRate} controls={false} disabled /> :
                        <Currency currency={ERP_CURRENCY} controls={false} disabled />
                    }
                </Form.Item>
            </div>
            <div className={styles.quotationReferenceWrapper}>
                <span className={styles.quotationReferenceLabel}>Quotation Reference:</span>
                <Form.Item name='quotation_price_reference' noStyle shouldUpdate>
                    <Radio.Group
                        options={[
                            { label: 'Website', value: 1, disabled: !websitePricesAvailable },
                            { label: 'Share point', value: 2 },
                            { label: 'Prices from Vendor', value: 3, disabled: !vendorPricesAvailable }
                        ]}
                        optionType="button"
                        buttonStyle="solid"
                        disabled={disabled}
                        className={styles.quotationReferenceGroup}
                    />
                </Form.Item>
            </div>
        </Form >
    )
}

VendorItemQuantityPriceRow.propTypes = {
    rowKey: PropTypes.string,
    quantity: PropTypes.number.isRequired,
    initialValues: PropTypes.object,
    onUpdate: PropTypes.func.isRequired,
    onEditingChange: PropTypes.func,
    requireFinisher: PropTypes.bool,
    vendorCurrencyCode: PropTypes.oneOf(['EUR', 'GBP', 'USD']).isRequired,
    projectCurrencyCode: PropTypes.oneOf(['EUR', 'GBP', 'USD']).isRequired,
    finisherCurrencyCode: PropTypes.oneOf(['EUR', 'GBP', 'USD']),
    canUpdateQuotation: PropTypes.bool,
    websitePricesAvailable: PropTypes.bool,
    vendorPricesAvailable: PropTypes.bool,
    disabled: PropTypes.bool,
}

const VendorItemPrices = ({ project_id, finisher, vendor, quantities, children, onVendorSelectionChange, onPriceUpdate, onVendorCommentChange, onEditingChange, onRemoveVendorFromProduct, item, canUpdateQuotation = true, disabled }) => {
    const { currency, itemDetails } = useLoaderData()
    const [vendorSelected, setVendorSelected] = useState(!!vendor.quotationAssignment)
    const [VendorData, setVendornData] = useState(null)
    const [isVendorModal, setIsVendorModalOpen] = useState(false);

    useEffect(() => {
        setVendorSelected(!!vendor.quotationAssignment)
    }, [vendor])

    const requireFinisher = !!finisher?.id //Finisher is involved
    const priceColumnsCount = 9 + (requireFinisher * 1)
    const vendorQuotationButtonText = () => {
        switch (vendor.quotationAssignment.vendor_quotation_status) {
            case ItemPriceVendorQuotationStatus.VENDOR_QUOTATION_SENT:
            case ItemPriceVendorQuotationStatus.VISUAL_SENT:
                return "View Sent Quotation"
            case ItemPriceVendorQuotationStatus.VENDOR_QUOTATION_RECEIVED:
                return "View Quotation"
            case ItemPriceVendorQuotationStatus.VENDOR_REQUESTED_VISUAL:
                return "Send Visual to Vendor"
            default:
                return ""
        }
    }

    const showVendorDetailsModal = async (id) => {
        const v = await getVendorDetails(id)
        setVendornData(v)
        setIsVendorModalOpen(true)
    }

    const handleVendorSelectionChange = (checked) => {
        setVendorSelected(checked)
        onVendorSelectionChange(vendor.id, checked)
    }

    const hasVat = vendor?.prices?.some(p => p.vat_rate !== null && p.vat_rate !== undefined && !isNaN(p.vat_rate))
    const isVatMissing = vendorSelected && (itemDetails?.vat_not_found || (vendor?.prices?.length > 0 && !hasVat))

    const isDefault = vendor.default === 1;
    const ribbon = isDefault ? 'Default' : 'Manual';
    const ribbonColor = isDefault ? '#14b877' : (vendorSelected ? '#fe7b1c' : '#1890ff');

    const sectionColorDark = vendorSelected ? '#fe7b1c' : (isDefault ? '#f5c53e' : '#91caff');
    const sectionColorLight = vendorSelected ? '#fff2e9' : (isDefault ? '#fef7e7' : '#f0f7ff');

    return (
        <div className={styles.container}>
            <SmallSection colorDark={sectionColorDark} colorLight={sectionColorLight} ribbon={ribbon} ribbonColor={ribbonColor}>
                {!quantities.length && <Alert message="Please select atleast one quantity" type="warning" showIcon />}
                {isVatMissing && (
                    <Alert
                        style={{ marginBottom: '0.75rem' }}
                        message="VAT rate not found for this product. Please check the product VAT configuration."
                        type="warning"
                        showIcon
                    />
                )}
                <table className={styles.vendorDetails}>
                    <colgroup>
                        <col width="50px" />
                    </colgroup>
                    <thead>
                        <tr>
                            <td></td>
                            <td>Vendor</td>
                            <td>Price List</td>
                            <td>Price List Link</td>
                            <td>Rating</td>
                            <td>Status</td>
                            {onRemoveVendorFromProduct && canUpdateQuotation && !disabled && <td></td>}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <th>
                                <Checkbox
                                    key={vendor.quotationAssignment?.id} //Forces rerender
                                    disabled={disabled || quantities.includes(0) || !quantities.length}
                                    checked={vendorSelected}
                                    onChange={(e) => {
                                        if (!e.target.checked) {
                                            Modal.confirm({
                                                content: 'The will remove the vendor prices.Do you really want to continue ?',
                                                onOk() {
                                                    handleVendorSelectionChange(e.target.checked)
                                                }
                                            })
                                        }
                                        else {
                                            handleVendorSelectionChange(e.target.checked)
                                        }

                                    }}
                                />
                            </th>
                            <th>
                                <Link to={generateRouteUrl('VendorUpdate', { id: vendor.id })} style={{ wordBreak: 'break-word' }}>{vendor.company}</Link>
                                {<InfoCircleOutlined onClick={() => {
                                    showVendorDetailsModal(vendor.id)
                                }} />}
                                <Modal title={VendorData?.company} onCancel={() => { setIsVendorModalOpen(false) }} open={isVendorModal} footer={null}>
                                    <div className={styles.vendorInfo}>
                                        <p>Contact Number :{VendorData?.mobile_number}</p>
                                        <p>Email : {VendorData?.email}</p>
                                        <p>No of projects done : {VendorData?.completed_project} </p>
                                    </div>
                                </Modal>
                            </th>
                            <th>{vendor.cms_vendor_info?.vendor_price_link ? 'Available' : 'Unavailable'}</th>
                            <th>{vendor.cms_vendor_info?.vendor_price_link && (
                                <Button href={vendor.cms_vendor_info.vendor_price_link} type="link" target="_blank">View</Button>
                            )}</th>
                            <th><Rate defaultValue={vendor.rating ?? 0} disabled /></th>
                            <th>{vendor.quotationAssignment?.vendor_quotation_status && getVendorQuotationStatus(vendor.quotationAssignment.vendor_quotation_status)}</th>
                            {onRemoveVendorFromProduct && canUpdateQuotation && !disabled && (
                                <th style={{ textAlign: 'right' }}>
                                    {!isDefault && Number(vendor.id) !== 852 && (
                                        <Button
                                            type="text"
                                            danger
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            onClick={() => {
                                                Modal.confirm({
                                                    title: `Remove ${vendor.company} from Product?`,
                                                    content: `Are you sure you want to remove ${vendor.company} from this product? It will be unassigned from this item and unlinked from the product.`,
                                                    okText: 'Remove',
                                                    okType: 'danger',
                                                    onOk() {
                                                        onRemoveVendorFromProduct(vendor.id)
                                                    }
                                                })
                                            }}
                                        >
                                            Remove
                                        </Button>
                                    )}
                                </th>
                            )}
                        </tr>
                    </tbody>
                </table>
            </SmallSection>
            {!!vendor.quotationAssignment && (
                <div>
                    {quantities.length > 0 && (
                        <div className={`${requireFinisher ? styles['quantityPricesHead--requireFinisher'] : styles.quantityPricesHead} vendor-price-row`} style={{ '--columns': priceColumnsCount }}>
                            <div>Quantity</div>
                            <div>Purchase Price</div>
                            {requireFinisher === true ? <div>Finisher&apos;s Price</div> : <div style={{ display: 'none' }}></div>}
                            <div>Handling Charges</div>
                            <div>Shipping Charges</div>
                            <div>Margin</div>
                            <div>Custom Selling Price/Unit</div>
                            <div>Sales Price </div>
                            <div>Total Price</div>
                            <div>Total Price (Incl. VAT)</div>
                        </div>
                    )}
                    {quantities.map(quantity => (
                        <VendorItemQuantityPriceRow
                            key={quantity}
                            rowKey={`vendor_${vendor.id}_qty_${quantity}`}
                            requireFinisher={requireFinisher}
                            finisherCurrencyCode={requireFinisher ? finisher.currency?.code : null}
                            quantity={quantity}
                            initialValues={
                                vendor.prices.find(p => p.quantity === quantity) ?? {
                                     margin: defaultProductMargin,
                                     conversion_rate: itemDetails.conversion_rate
                                }
                            }
                            onUpdate={(prices) => {
                                onPriceUpdate(vendor.id, quantity, prices)
                            }}
                            onEditingChange={onEditingChange}
                            vendorCurrencyCode={vendor.currency.code}
                            projectCurrencyCode={currency.code}
                            canUpdateQuotation={canUpdateQuotation}
                            websitePricesAvailable={!!vendor.cms_vendor_info?.price_id}
                            vendorPricesAvailable={vendor.quotationAssignment.vendor_quotation_status >= ItemPriceVendorQuotationStatus.VENDOR_QUOTATION_SENT}
                            disabled={disabled || (itemDetails.approved_quantity && quantity !== itemDetails.approved_quantity)}
                        />
                    ))}
                    <Form layout="vertical" initialValues={vendor.quotationAssignment} onValuesChange={(_, values) => onVendorCommentChange(vendor.id, values)}>
                        <Form.Item name="comment" label="Special Comment for Vendor">
                            <Input.TextArea disabled={disabled} />
                        </Form.Item>
                    </Form>
                    <div className="actions align--left">
                        <VendorQuotationForm vendor={vendor} disabled={disabled} />
                        {vendor.quotationAssignment.vendor_quotation_status >= ItemPriceVendorQuotationStatus.VENDOR_QUOTATION_SENT && (
                            <Link to={generateRouteUrl('VendorQuotation', {
                                projectId: project_id,
                                itemId: item,
                                vendorId: vendor.id
                            })}>
                                <Button>{vendorQuotationButtonText()}</Button>
                            </Link>
                        )}
                    </div>
                </div>
            )}
            {children}
            <Outlet />
        </div>
    )
}
VendorItemPrices.propTypes = {
    project_id: PropTypes.number,
    vendor: PropTypes.object.isRequired,
    item: PropTypes.number,
    selectedVendorId: PropTypes.number,
    quantities: PropTypes.arrayOf(Number).isRequired,
    onVendorSelectionChange: PropTypes.func.isRequired,
    onPriceUpdate: PropTypes.func.isRequired,
    onVendorCommentChange: PropTypes.func.isRequired,
    onEditingChange: PropTypes.func,
    onRemoveVendorFromProduct: PropTypes.func,
    children: PropTypes.node,
    finisher: PropTypes.object,
    canUpdateQuotation: PropTypes.bool,
    disabled: PropTypes.bool,
}
export default VendorItemPrices
